/**
 * This file was created to store methods that manipulate time using TypeScript.
 * In the future, all methods from `time.js` should be moved to this file.
 * To reduce review time, we opted to create a new TypeScript file for the new methods.
 */
/**
 * Converts a local datetime (from ISO string) in a specific timezone to UTC epoch seconds.
 * This interprets the provided ISO string as a wall-clock time in the given timezone,
 * and returns the corresponding UTC epoch seconds.
 *
 * Note: The function interprets the ISO string as a local time in the given timezone,
 * not as a UTC time.
 *
 * @param isoString - ISO 8601 datetime string (e.g., "2023-12-12T14:30:00.000Z")
 * @param timezone - IANA timezone string (e.g., "America/New_York")
 * @returns Unix epoch seconds (UTC)
 * @throws {TypeError} If isoString or timezone are not valid strings
 */
export function convertLocalTimeToUTCEpoch(isoString, timezone) {
    if (typeof isoString !== 'string' || typeof timezone !== 'string') {
        throw new TypeError('isoString and timezone must be strings');
    }
    // Validate date
    const date = new Date(isoString);
    if (isNaN(date.getTime())) {
        throw new TypeError('isoString is not a valid date string');
    }
    // Validate timezone (basic check)
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: timezone });
    }
    catch {
        throw new TypeError('timezone is not a valid IANA timezone string');
    }
    // Get the components as they appear in the browser's local timezone
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    // Now we have the wall clock time the user saw (e.g., 00:00:00)
    // We need to convert this to UTC as if it were in the facility timezone
    // Create a string representation of this time
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    const dateTimeString = `${dateStr}T${timeStr}`;
    // Interpret this wall clock time as if it's in the facility timezone
    // We'll use a trick: format different UTC times in the target timezone until we find
    // the one that produces our desired wall clock time
    // Start with a rough estimate (treat as UTC)
    let estimateUTC = new Date(`${dateTimeString}Z`).getTime();
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });
    // See what this UTC time looks like in the facility timezone
    const formatted = formatter.format(new Date(estimateUTC));
    // 'en-US' format: MM/DD/YYYY, HH:MM:SS
    const [datePart, timePart] = formatted.split(', ');
    const [m, d, y] = datePart.split('/').map(Number);
    const [h, min, s] = timePart.split(':').map(Number);
    // Calculate the offset needed
    const gotTime = new Date(Date.UTC(y, m - 1, d, h, min, s)).getTime();
    const wantedTime = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds)).getTime();
    const offset = wantedTime - gotTime;
    // Apply offset to get the correct UTC time
    const correctUTC = estimateUTC + offset;
    return Math.floor(correctUTC / 1000);
}
